import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useDeals } from '../../../store/DealsContext';
import { formatCurrency } from '../../../utils/formatters';
import { useModelExport } from '../../../hooks/useModelExport';

interface LBOAnalysisProps {
  onClose: () => void;
}

export default function LBOAnalysis({ onClose }: LBOAnalysisProps) {
  const { deals } = useDeals();
  const { exportToExcel, exporting } = useModelExport();
  const [selectedDeal, setSelectedDeal] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [equityContribution, setEquityContribution] = useState('');
  const [debtAmount, setDebtAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [holdingPeriod, setHoldingPeriod] = useState('');
  const [results, setResults] = useState<any>(null);

  const handleCalculate = () => {
    const initialInvestment = Number(equityContribution);
    const totalDebt = Number(debtAmount);
    const annualInterest = totalDebt * (Number(interestRate) / 100);
    
    const exitValue = Number(purchasePrice) * Math.pow(1.15, Number(holdingPeriod));
    const debtPaydown = totalDebt * 0.1 * Number(holdingPeriod);
    const remainingDebt = totalDebt - debtPaydown;
    
    const equityValue = exitValue - remainingDebt;
    const irr = Math.pow(equityValue / initialInvestment, 1 / Number(holdingPeriod)) - 1;
    
    setResults({
      exitValue,
      equityValue,
      irr: irr * 100,
      moic: equityValue / initialInvestment
    });
  };

  const handleExport = () => {
    if (!results) return;
    exportToExcel({
      inputs: {
        purchasePrice,
        equityContribution,
        debtAmount,
        interestRate,
        holdingPeriod
      },
      results
    }, 'lbo-analysis.xlsx');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">LBO Analysis</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Deal</label>
                <select
                  value={selectedDeal}
                  onChange={(e) => {
                    setSelectedDeal(e.target.value);
                    const deal = deals.find(d => d.id === e.target.value);
                    if (deal) setPurchasePrice(String(deal.value));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select Deal</option>
                  {deals.map((deal) => (
                    <option key={deal.id} value={deal.id}>{deal.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Purchase Price</label>
                <input
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Equity Contribution</label>
                <input
                  type="number"
                  value={equityContribution}
                  onChange={(e) => setEquityContribution(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Debt Amount</label>
                <input
                  type="number"
                  value={debtAmount}
                  onChange={(e) => setDebtAmount(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Interest Rate (%)</label>
                <input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Holding Period (years)</label>
                <input
                  type="number"
                  value={holdingPeriod}
                  onChange={(e) => setHoldingPeriod(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={handleCalculate}
                disabled={!selectedDeal}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Calculate
              </button>
            </div>

            {results && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Results</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Exit Value</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.exitValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Equity Value</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.equityValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">IRR</span>
                    <span className="text-sm font-medium text-gray-900">
                      {results.irr.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">MOIC</span>
                    <span className="text-sm font-medium text-gray-900">
                      {results.moic.toFixed(2)}x
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="w-full px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                >
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}